import React from 'react'
import type { SongSettings } from '../../../../types/song'
import { PROSODY_STYLE_OPTIONS } from '../../../../types/song'
import type { RhymeSchemePattern } from '../../../../types/rhymeScheme'
import { SectionDivider } from '../../shared/SectionDivider'
import { GradientBanner } from '../../shared/GradientBanner'
import { RhymeSchemeSelector } from '../../RhymeSchemeSelector'

interface SoundTabProps {
  settings: SongSettings
  onUpdateNestedSettings: <T extends Record<string, unknown>>(
    parentKey: keyof SongSettings,
    childKey: keyof T,
    value: unknown
  ) => void
  // Legacy props kept for interface compatibility, but no longer used for rhyme preferences
  onAddToArray?: (parentKey: keyof SongSettings, childKey: string, value: string) => void
  onRemoveFromArray?: (parentKey: keyof SongSettings, childKey: string, value: string) => void
}

/**
 * Sound tab for song settings
 *
 * Provides rhyme scheme configuration (per section type) and prosody settings.
 * Maps to SongSettings.rhyme_schemes and SongSettings.prosody_settings.
 *
 * Note: Legacy rhyme_preferences fields have been deprecated as of Scope 21.
 * Per-section-type scheme configuration replaces the old toggles and checkboxes.
 */
export const SoundTab: React.FC<SoundTabProps> = ({
  settings,
  onUpdateNestedSettings,
}) => {
  // Handler for rhyme scheme updates - updates entire rhyme_schemes object per section
  const handleRhymeSchemeUpdate = (sectionType: string, pattern: RhymeSchemePattern, customPattern?: string) => {
    const schemeValue = {
      pattern,
      ...(pattern === 'CUSTOM' && customPattern ? { customPattern } : {}),
    }

    // Update the specific section's scheme within the rhyme_schemes object
    onUpdateNestedSettings('rhyme_schemes', sectionType as never, schemeValue)
  }

  return (
    <div className="space-y-6">
      <GradientBanner
        title="Sound & Rhythm"
        description="Control rhyme schemes, prosody patterns, and rhythmic qualities of your lyrics."
        icon="🎵"
        variant="info"
      />

      {/* Rhyme Scheme Configuration Section (Scope 21)
        * Replaces legacy rhyme_preferences toggles with per-section-type scheme configuration.
        * Users can now set expected patterns (AABB, ABAB, etc.) for each section type.
        */}
      <RhymeSchemeSelector
        settings={settings}
        onUpdateRhymeScheme={handleRhymeSchemeUpdate}
      />

      <SectionDivider label="Prosody & Rhythm" />

      {/* Prosody Settings Section */}
      <div>
        <h3 className="text-heading font-medium text-gray-900 mb-3">Prosody Settings</h3>

        {/* Rhythmic Stability */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rhythmic Stability: {settings.prosody_settings.rhythmic_stability}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={settings.prosody_settings.rhythmic_stability}
            onChange={(e) => onUpdateNestedSettings('prosody_settings', 'rhythmic_stability', parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Varied</span>
            <span>Rigid</span>
          </div>
        </div>

        {/* Phrasing Style */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phrasing Style
          </label>
          <select
            value={settings.prosody_settings.phrasing_style}
            onChange={(e) => onUpdateNestedSettings('prosody_settings', 'phrasing_style', e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white"
          >
            {PROSODY_STYLE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Meter Consistency */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Meter Consistency: {settings.prosody_settings.meter_consistency}
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={settings.prosody_settings.meter_consistency}
            onChange={(e) => onUpdateNestedSettings('prosody_settings', 'meter_consistency', parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Free Verse</span>
            <span>Strict Meter</span>
          </div>
        </div>

        {/* Syllable Emphasis */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.prosody_settings.syllable_emphasis}
              onChange={(e) => onUpdateNestedSettings('prosody_settings', 'syllable_emphasis', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 mr-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Consider syllable stress patterns</span>
          </label>
        </div>
      </div>
    </div>
  )
}
